import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { LimitedTimePromoBanner, isPromoActive } from "@/components/LimitedTimePromoBanner";

const NAV_LINKS = [
  { href: "#rooms", label: "Rooms" },
  { href: "#offers", label: "Offers" },
  { href: "#gallery", label: "Gallery" },
  { href: "#attractions", label: "Attractions" },
  { href: "#dining", label: "Dining" },
  { href: "#kids", label: "Kids Activities" },
  { href: "#testimonials", label: "Reviews" },
  { href: "#inquiry", label: "Group Inquiry" },
  { href: "#blog", label: "Blog" },
  { href: "#faq", label: "FAQ" },
  { href: "#contact", label: "Info" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const promoVisible = isPromoActive();
  // Welcome bar = h-10 (40px).
  // Promo banner = h-14 (56px) on mobile, h-10 (40px) on sm+ to fit full text on phones.
  // Nav offsets stack everything cleanly with no overlap.
  const navTopClass = promoVisible ? "top-[96px] sm:top-20" : "top-10";

  return (
    <>
      <div
        className="fixed top-0 w-full z-50 bg-[#1E407C] text-white h-10 flex items-center"
        data-testid="top-welcome-bar"
      >
        <div className="relative max-w-7xl mx-auto px-3 sm:px-4 w-full flex items-center justify-center gap-3 sm:gap-4">
          <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0" />
          <p className="font-['Playfair_Display'] text-sm sm:text-base md:text-lg text-center tracking-wide">
            Welcome to your Home Away from Home
          </p>
          <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0" />
          <a
            href="tel:+18157307500"
            className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 bg-[#D4AF37] hover:bg-[#B5952F] text-white h-7 px-2.5 sm:px-3 rounded-full font-bold text-[10px] sm:text-xs uppercase tracking-wider transition-colors"
            data-testid="top-call-link"
            aria-label="Call Best Western Joliet Inn & Suites at (815) 730-7500"
          >
            <Phone size={12} className="shrink-0" />
            <span className="hidden sm:inline">Call</span>
          </a>
        </div>
      </div>

      {/* Sticky promo banner — sits just below welcome bar, above main nav */}
      <LimitedTimePromoBanner />

      <nav
        className={`fixed ${navTopClass} w-full z-40 transition-all ${
          scrolled ? "glass shadow-lg" : "bg-black/30 backdrop-blur-sm"
        }`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center h-14">
            <div className="hidden lg:flex items-center gap-6">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  className={`font-semibold text-sm uppercase tracking-wider ${
                    scrolled
                      ? "text-[#1E407C] hover:text-[#D4AF37]"
                      : "text-white hover:text-[#D4AF37]"
                  }`}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </a>
              ))}
            </div>
            <button
              className="lg:hidden p-2 absolute right-4"
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {menuOpen ? (
                <X
                  className={scrolled ? "text-[#1E407C]" : "text-white"}
                  size={24}
                />
              ) : (
                <Menu
                  className={scrolled ? "text-[#1E407C]" : "text-white"}
                  size={24}
                />
              )}
            </button>
          </div>

          {menuOpen && (
            <div
              className="lg:hidden glass rounded-lg mb-4 p-4"
              data-testid="mobile-menu"
            >
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  className="block py-3 text-[#1E407C] font-semibold border-b border-gray-100"
                  onClick={() => setMenuOpen(false)}
                >
                  {l.label}
                </a>
              ))}
            </div>
          )}
        </div>
      </nav>
    </>
  );
};
